/**
 * Pure helper functions for the closure redirect system.
 *
 * Extracted from index.js for testability. All functions are stateless
 * and have no side effects (except getCookie which reads document.cookie).
 */

/**
 * Validate a URL string for redirect safety.
 *
 * Accepts relative URLs (starting with /) and valid absolute URLs.
 * Rejects empty strings, malformed URLs, and dangerous protocols.
 *
 * @param {string} string - URL to validate.
 * @return {boolean} True if the URL is valid for redirect.
 */
export function isValidUrl( string ) {
	if ( string.startsWith( '/' ) && ! string.startsWith( '//' ) ) {
		return true;
	}

	try {
		var parsed = new URL( string );
		// Only allow http and https protocols — block javascript:, data:, etc.
		return 'http:' === parsed.protocol || 'https:' === parsed.protocol;
	} catch ( _ ) {
		return false;
	}
}

/**
 * Validate a client ID format.
 *
 * Must be exactly 32 lowercase hex characters to match the PHP-side
 * validation in Closure_Redirect_Storage::get_redirect_identifier().
 *
 * @param {string} id - Client ID to validate.
 * @return {boolean} True if the ID matches the expected format.
 */
export function isValidClientId( id ) {
	return /^[a-f0-9]{32}$/.test( id );
}

/**
 * Generate a random 32-character hex string for use as a session client ID.
 *
 * Uses crypto.getRandomValues for cryptographic randomness.
 *
 * @return {string} 32-character lowercase hex string.
 */
export function generateClientId() {
	var array = new Uint8Array( 16 );
	crypto.getRandomValues( array );
	return Array.from( array, function( byte ) {
		return byte.toString( 16 ).padStart( 2, '0' );
	} ).join( '' );
}

/**
 * Read a cookie value by name.
 *
 * @param {string} cname - Cookie name.
 * @return {string} Cookie value or empty string if not found.
 */
export function getCookie( cname ) {
	var name = cname + '=';
	var decodedCookie = decodeURIComponent( document.cookie );
	var ca = decodedCookie.split( ';' );
	for ( var i = 0; i < ca.length; i++ ) {
		var c = ca[ i ].trim();
		if ( 0 === c.indexOf( name ) ) {
			return c.substring( name.length );
		}
	}
	return '';
}

/**
 * Create a hidden link element for programmatic navigation.
 *
 * Appends a hidden anchor to document.body with security attributes.
 *
 * @param {string} id - Element ID for the link.
 * @return {HTMLAnchorElement} The created link element.
 */
export function createLink( id ) {
	var a = document.createElement( 'a' );
	a.id = id;
	a.style.display = 'none';
	a.rel = 'noopener noreferrer';
	document.body.appendChild( a );
	return a;
}

/**
 * Get or generate a per-tab identifier stored in sessionStorage.
 *
 * sessionStorage is per-tab by browser design — each tab gets its own
 * isolated storage. This ensures redirect transients are scoped to the
 * tab that triggered the recipe, preventing wrong-tab redirects.
 *
 * @return {string} 32-character lowercase hex tab ID.
 */
export function getTabId() {
	var key = 'automator_tab_id';
	var tabId = sessionStorage.getItem( key );

	if ( ! tabId || ! isValidClientId( tabId ) ) {
		tabId = generateClientId();
		sessionStorage.setItem( key, tabId );
	}

	return tabId;
}

/**
 * Ensure a session client ID cookie exists for redirect coordination.
 *
 * Reads the automator_client_id cookie; if missing or invalid, generates
 * a new 32-character hex ID and writes it as a session cookie.
 *
 * GDPR: Session-only cookie (deleted on browser close).
 * Classified as "strictly necessary" for redirect functionality.
 */
export function ensureClientId() {
	var clientId = getCookie( 'automator_client_id' );

	if ( ! clientId || ! isValidClientId( clientId ) ) {
		clientId = generateClientId();
	}

	// Store in session cookie (no expiry = deleted on browser close)
	// No localStorage used (GDPR compliance - no persistent tracking)
	document.cookie = 'automator_client_id=' + clientId + '; path=/; SameSite=Lax';
}

/**
 * Check whether a URL targets the same origin as the current page.
 *
 * Relative URLs (starting with / but not //) are always same-origin.
 * Absolute URLs are compared against window.location.origin.
 *
 * @param {string} url - URL to check.
 * @return {boolean} True if the URL is same-origin.
 */
export function isSameOrigin( url ) {
	if ( ! url ) {
		return false;
	}
	// Relative path — always same-origin.
	if ( '/' === url.charAt( 0 ) && '/' !== url.charAt( 1 ) ) {
		return true;
	}
	try {
		return new URL( url ).origin === window.location.origin;
	} catch ( e ) {
		return false;
	}
}

/**
 * Determine whether an AJAX response should be inspected for redirect headers.
 *
 * Filters out cross-origin requests, heartbeat, autosave, and our own
 * redirect-check action to avoid unnecessary processing.
 *
 * @param {Object} settings - jQuery AJAX settings object.
 * @return {boolean} True if the response should be inspected.
 */
export function shouldInspectAjaxResponse( settings ) {
	if ( ! settings.url || ! isSameOrigin( settings.url ) ) {
		return false;
	}

	var dataStr = typeof settings.data === 'string' ? settings.data : '';
	var url = settings.url + dataStr;

	if ( -1 !== url.indexOf( 'heartbeat' ) ||
		-1 !== url.indexOf( 'autosave' ) ||
		-1 !== url.indexOf( 'automator_check_closure_redirect' ) ) {
		return false;
	}

	return true;
}
